// ติดตามนักเรียนขาดเรียน (ภาคศาสนา) — หัวหน้าวิชาการศาสนา (pos 57)
//
// คู่ขนานกับ discipline/DisciplineAbsenceMonitor.js ของภาคสามัญ แต่มีสิ่งที่ของเดิมไม่มี:
// ป้าย "มีใบลา" บนแถวที่ถูกกาว่าขาดทั้งที่นักเรียนแจ้งลาไว้แล้ว
//
// นั่นคือเรื่องที่หัวหน้าวิชาการต้องจับ ไม่ใช่ตัวเลขขาดเฉย ๆ — มันแปลว่าครูผู้สอน
// ไม่เห็นใบลาตอนเช็คชื่อ หรือใบลามาทีหลัง ทั้งสองกรณีต้องกลับไปแก้แถวนั้น
// ไม่ใช่ปล่อยให้นักเรียนรับผลว่าขาดทั้งที่ลาถูกต้อง
import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Button, Card, Col, Form, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { useLanguage } from '../contexts/LanguageContext'
import { StudentName, studentDisplayName } from '../utils/studentName'
import th from '../locales/islamic/th'
import jawi from '../locales/islamic/jawi'
import {
  API, ATT_STATUS, AcademicFilterBar, DateRangeBar, StatTile, StatusDot, StudentAvatar,
  alertRequestError, roomLabel, thaiDate, useAcademicFilters, useDarkTables, useDateRange,
  useStudyYears,
} from './academicShared'

// สถานะที่หน้านี้เลือกดูได้ — ต้องตรงกับ MONITOR_STATUSES ฝั่ง server (ค่าที่ไม่รู้จักถูกตอบ 400)
// ไม่มี 'present' โดยตั้งใจ: คาบที่มาเรียนคือเกือบทุกแถวของช่วงนั้น เปิดให้เลือกเมื่อไหร่
// หน้านี้จะกลายเป็นการดัมป์ตารางเช็คชื่อทั้งช่วง ซึ่งไม่ใช่คำถามที่หน้านี้ตอบ
// เรียงจากหนักไปเบา และคงลำดับนี้ไว้เสมอเพื่อให้ query string นิ่ง ไม่ยิงซ้ำเพราะสลับที่
const MONITOR_STATUSES = ['absent', 'late', 'leave']
const STATUS_META = Object.fromEntries(ATT_STATUS.map(s => [s.key, s]))

// สีพื้นแถวตามสถานะ — แดงคือขาด เหลืองคือสาย ฟ้าคือลา
// สีอย่างเดียวบอกความหมายไม่ได้ (เหลืองกับฟ้าคอนทราสต์ต่ำ และคนตาบอดสีแยกแดง/เหลืองยาก)
// ช่อง "สถานะ" จึงขึ้นคู่กันเสมอเมื่อเลือกดูหลายสถานะ ส่วนเลือกอันเดียวคือทั้งตาราง
// เป็นสถานะนั้นอยู่แล้ว สีทำหน้าที่แค่บอกว่ากำลังดูอะไรอยู่
const ROW_CLASS = { absent: 'table-danger', late: 'table-warning', leave: 'table-info' }

const EMPTY = {
  range: { start: '', end: '' }, statuses: ['absent'], rows: [], byStudent: [],
  summary: { periods: 0, students: 0, withLeave: 0, byStatus: { absent: 0, late: 0, leave: 0 } },
}

// ป้ายบอกว่าแถวนี้นักเรียนมีใบลาครอบอยู่ — ใช้สีเตือน ไม่ใช่สีอันตราย เพราะคนผิด
// ไม่ใช่นักเรียน และเป็นเรื่องที่แก้ได้ด้วยการกลับไปแก้การเช็คชื่อ
// ใบลามาจากฝั่งไหนต้องบอกด้วย ไม่ใช่แค่ประเภท — "ครูศาสนาแจ้งแต่ครูสามัญกาขาด"
// กับ "ฝ่ายปกครองอนุมัติใบกลับบ้านแล้วแต่ครูยังกาขาด" เป็นคนละเรื่องที่ต้องไปคุย
// คนละที่ ถ้าเห็นแค่ "มีใบลา" ก็ยังไม่รู้ว่าจะเริ่มตามจากตรงไหน
//
// แถวที่ครูกาว่า "ลา" อยู่แล้วไม่ใช่เรื่องที่ต้องเตือน — ใบตรงกับที่กา บอกแค่ว่าใบมาจาก
// ฝ่ายไหนก็พอ ถ้าขึ้นป้ายเหลืองด้วยจะกลายเป็นว่าทุกแถวของสถานะ "ลา" ดูเหมือนมีปัญหา
//
// ป้ายนี้คือที่เดียวที่บอกว่าแถวไหนถูกกาผิด ตั้งแต่สีพื้นแถวถูกใช้บอกสถานะแทน
const LeaveFlag = ({ t, row }) => {
  if (!row.leave_type) {
    // ครูกาว่า "ลา" แต่ไม่มีใบลาครอบวันนั้น = ไม่มีใครแจ้งลาไว้ ซึ่งเป็นคำตอบหนึ่งของ
    // คำถาม "ใครแจ้งลา" ไม่ใช่ข้อมูลที่หายไป — บอกไว้เฉพาะแถวสถานะ "ลา" เพราะแถวขาด/สาย
    // ที่ไม่มีใบลาคือเรื่องปกติ ไม่ต้องกำกับอะไร
    return row.att_status === 'leave'
      ? <span className="fs-xs text-secondary ms-1">{t.academic.absence.noLeaveDoc}</span>
      : null
  }
  const type = t.academic.leave.type[row.leave_type] || row.leave_type
  const dept = t.academic.leave.source[row.leave_source]
  // ใบกลับบ้านชื่อประเภทกับชื่อฝ่ายเป็นเรื่องเดียวกัน เขียนคู่กันได้ "กลับบ้าน · ใบกลับบ้าน"
  const label = !dept ? type
    : row.leave_source === 'home_leave' ? dept
    : `${type} · ${dept}`
  if (!row.leave_mismatch) {
    return <span className="fs-xs text-secondary ms-1">{t.academic.absence.matchedLeave(label)}</span>
  }
  return (
    <Badge bg="warning" text="dark" className="fs-xs ms-1">
      <i className="ri-information-line me-1" />
      {t.academic.absence.hasLeave(label)}
    </Badge>
  )
}

// สถานะของแถว — จุดสีนำหน้าชื่อสถานะเสมอ ไม่ให้สีเป็นตัวบอกความหมายตัวเดียว
// (เหลืองของ "สาย" กับฟ้าของ "ลา" คอนทราสต์ต่ำเกินกว่าจะอ่านออกทั้งสองธีม)
const StatusCell = ({ t, status }) => {
  const meta = STATUS_META[status]
  return (
    <span className="d-inline-flex align-items-center gap-1 fs-sm">
      {meta && <StatusDot color={meta.color} />}
      {t.academic.attendance[status] || status}
    </span>
  )
}

export default function IslamicAbsenceMonitor() {
  const { setLoading } = useLoading()
  const { lang } = useLanguage()
  const t = lang === 'th' ? th : jawi
  const navigate = useNavigate()
  useDarkTables()

  const { years, yearId, setYearId } = useStudyYears()
  const filters = useAcademicFilters(yearId, t)
  const range = useDateRange('day')
  const [data, setData] = useState(EMPTY)
  const [statuses, setStatuses] = useState(['absent'])
  const [onlyWithLeave, setOnlyWithLeave] = useState(false)
  const [view, setView] = useState('period')   // 'period' รายคาบ · 'student' สรุปรายคน

  const statusParam = statuses.join(',')

  useEffect(() => {
    if (!yearId) return
    let alive = true
    setLoading(true)
    const q = new URLSearchParams(filters.query.replace(/^\?/, ''))
    q.set('start', range.start)
    q.set('end', range.end)
    q.set('status', statusParam)
    axiosInstance.get(`${API}/islamic/academic/absence/${yearId}?${q}`)
      .then(res => { if (alive) setData(res.data || EMPTY) })
      .catch(err => { if (alive) { setData(EMPTY); alertRequestError(t, err, t.common.error) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [yearId, filters.query, range.start, range.end, statusParam, setLoading, t])

  const { summary, rows, byStudent } = data
  const multiDay = range.start !== range.end

  // ปิดครบทุกสถานะแล้วตารางจะว่างโดยไม่มีเหตุผลให้อ่าน — คงตัวสุดท้ายไว้เสมอ
  const toggleStatus = (key) => setStatuses(prev => {
    const next = prev.includes(key)
      ? prev.filter(s => s !== key)
      : MONITOR_STATUSES.filter(s => prev.includes(s) || s === key)
    return next.length ? next : prev
  })

  const statusText = statuses.map(k => t.academic.attendance[k]).join('/')
  const breakdown = statuses.length > 1
    ? statuses.map(k => `${t.academic.attendance[k]} ${(summary.byStatus?.[k] || 0).toLocaleString('th-TH')}`).join(' · ')
    : null

  const visibleRows = useMemo(
    () => (onlyWithLeave ? rows.filter(r => r.leave_mismatch) : rows), [rows, onlyWithLeave])
  const visibleStudents = useMemo(
    () => (onlyWithLeave ? byStudent.filter(s => s.withLeave > 0) : byStudent), [byStudent, onlyWithLeave])

  return (
    <React.Fragment>
      <div className="mb-3">
        <ol className="breadcrumb fs-sm mb-1">
          <li className="breadcrumb-item">{t.academic.groupTitle}</li>
          <li className="breadcrumb-item active">{t.academic.absence.pageTitle}</li>
        </ol>
        <h4 className="main-title mb-0">{t.academic.absence.pageTitle}</h4>
      </div>

      <Card className="card-one mb-3">
        <Card.Body>
          <DateRangeBar t={t} range={range} />
          <hr className="my-3" />
          <AcademicFilterBar t={t} years={years} yearId={yearId} setYearId={setYearId} filters={filters}>
            <Col xs={12} md={6} lg={4}>
              <Form.Label className="fs-xs text-secondary mb-1">{t.academic.absence.filterStatus}</Form.Label>
              <div className="btn-group btn-group-sm d-flex" role="group">
                {MONITOR_STATUSES.map(key => {
                  const on = statuses.includes(key)
                  return (
                    <Button
                      key={key}
                      variant={on ? 'primary' : 'outline-secondary'}
                      aria-pressed={on}
                      onClick={() => toggleStatus(key)}
                    >
                      <span className="d-inline-flex align-items-center gap-1">
                        <StatusDot color={STATUS_META[key].color} size={8} />
                        {t.academic.attendance[key]}
                      </span>
                    </Button>
                  )
                })}
              </div>
            </Col>
          </AcademicFilterBar>
        </Card.Body>
      </Card>

      <div className="row g-2 mb-3">
        <Col xs={12} md={4}>
          <StatTile
            label={t.academic.absence.statPeriodsOf(statusText)}
            value={summary.periods.toLocaleString('th-TH')}
            sub={breakdown}
          />
        </Col>
        <Col xs={6} md={4}>
          <StatTile
            label={t.academic.absence.statStudentsOf(statusText)}
            value={summary.students.toLocaleString('th-TH')}
          />
        </Col>
        <Col xs={6} md={4}>
          <StatTile
            color={summary.withLeave > 0 ? '#ffc107' : undefined}
            label={t.academic.absence.statWithLeave}
            value={summary.withLeave.toLocaleString('th-TH')}
            sub={summary.withLeave > 0 ? t.academic.absence.withLeaveHint : null}
          />
        </Col>
      </div>

      <Card className="card-one">
        <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn btn-${view === 'period' ? 'primary' : 'outline-secondary'}`}
              onClick={() => setView('period')}
            >
              {t.academic.absence.viewPeriod}
            </button>
            <button
              type="button"
              className={`btn btn-${view === 'student' ? 'primary' : 'outline-secondary'}`}
              onClick={() => setView('student')}
            >
              {t.academic.absence.viewStudent}
            </button>
          </div>
          {summary.withLeave > 0 && (
            <Form.Check
              type="switch"
              className="fs-sm mb-0"
              checked={onlyWithLeave}
              onChange={e => setOnlyWithLeave(e.target.checked)}
              label={t.academic.absence.onlyWithLeave(summary.withLeave)}
            />
          )}
        </Card.Header>
        <Card.Body className="p-0">
          {(view === 'period' ? visibleRows : visibleStudents).length === 0 ? (
            <div className="text-center text-secondary py-5 fs-sm">{t.academic.absence.empty}</div>
          ) : view === 'period' ? (
            <div className="table-responsive">
              <Table className="table-sm mb-0 align-middle table-hover">
                <thead>
                  <tr>
                    {multiDay && <th style={{ width: 120 }}>{t.academic.attendance.colDate}</th>}
                    <th style={{ width: 60 }}>{t.academic.attendance.colPeriod}</th>
                    {/* ช่องสถานะขึ้นเฉพาะตอนเลือกดูหลายสถานะ — เลือกอย่างเดียวคือทั้งตาราง
                        เป็นสถานะนั้นอยู่แล้ว ช่องที่ซ้ำกันทุกแถวมีแต่กินที่ */}
                    {statuses.length > 1 && <th style={{ width: 90 }}>{t.academic.absence.colStatus}</th>}
                    <th>{t.academic.attendance.colStudent}</th>
                    <th style={{ width: 100 }}>{t.academic.attendance.colRoom}</th>
                    <th style={{ width: 90 }}>{t.academic.absence.colRegularClass}</th>
                    <th style={{ width: 110 }}>{t.academic.absence.colDorm}</th>
                    <th>{t.academic.attendance.colSubject}</th>
                    <th style={{ width: 160 }}>{t.academic.attendance.colTeacher}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(r => (
                    <tr
                      key={r.is_att_id}
                      className={ROW_CLASS[r.att_status]}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/islamic/student-tracking/${r.st_id}`)}
                    >
                      {multiDay && <td className="fs-sm">{thaiDate(r.date)}</td>}
                      <td className="fs-sm">{r.period_order ?? '-'}</td>
                      {statuses.length > 1 && <td><StatusCell t={t} status={r.att_status} /></td>}
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <StudentAvatar photo={r.st_photo} name={studentDisplayName(r, lang)} size={28} />
                          <div style={{ minWidth: 0 }}>
                            <div className="fs-sm">
                              <StudentName student={r} lang={lang} />
                              <LeaveFlag t={t} row={r} />
                            </div>
                            <div className="fs-xs text-secondary">{r.st_idnumber}</div>
                          </div>
                        </div>
                      </td>
                      <td className="fs-sm">{roomLabel(r)}</td>
                      <td className="fs-sm">{r.regular_class || <span className="text-secondary">-</span>}</td>
                      <td className="fs-sm">{r.dorm_name || <span className="text-secondary">-</span>}</td>
                      <td className="fs-sm">{r.subject || <span className="text-secondary">-</span>}</td>
                      <td className="fs-sm">{r.teacher || <span className="text-secondary">-</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table-sm mb-0 align-middle table-hover">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>#</th>
                    <th>{t.academic.attendance.colStudent}</th>
                    <th style={{ width: 100 }}>{t.academic.attendance.colRoom}</th>
                    <th style={{ width: 90 }}>{t.academic.absence.colRegularClass}</th>
                    <th style={{ width: 110 }}>{t.academic.absence.colDorm}</th>
                    {/* หนึ่งช่องต่อหนึ่งสถานะที่เลือก — ยอดรวมอย่างเดียวตอบไม่ได้ว่า
                        20 คาบของคนนี้คือขาด 18 หรือลาถูกต้อง 18 ซึ่งคนละเรื่องกันคนละขั้ว */}
                    {statuses.map(k => (
                      <th key={k} className="text-end" style={{ width: 80 }}>
                        {t.academic.attendance[k]}
                      </th>
                    ))}
                    {statuses.length > 1 && (
                      <th className="text-end" style={{ width: 70 }}>{t.academic.absence.colTotal}</th>
                    )}
                    <th className="text-end" style={{ width: 80 }}>{t.academic.absence.colDays}</th>
                    <th className="text-end" style={{ width: 100 }}>{t.academic.absence.colWithLeave}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleStudents.map((s, i) => (
                    <tr
                      key={`${s.st_id}-${s.ir_id}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/islamic/student-tracking/${s.st_id}`)}
                    >
                      <td className="text-secondary fs-xs">{i + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <StudentAvatar photo={s.st_photo} name={studentDisplayName(s, lang)} size={28} />
                          <div style={{ minWidth: 0 }}>
                            <div className="fs-sm text-truncate"><StudentName student={s} lang={lang} /></div>
                            <div className="fs-xs text-secondary">{s.st_idnumber}</div>
                          </div>
                        </div>
                      </td>
                      <td className="fs-sm">{roomLabel(s)}</td>
                      <td className="fs-sm">{s.regular_class || <span className="text-secondary">-</span>}</td>
                      <td className="fs-sm">{s.dorm_name || <span className="text-secondary">-</span>}</td>
                      {statuses.map(k => (
                        <td
                          key={k}
                          className={`text-end fs-sm${k === 'absent' ? ' fw-semibold' : ''}`}
                          style={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {s[k] || <span className="text-secondary">-</span>}
                        </td>
                      ))}
                      {statuses.length > 1 && (
                        <td className="text-end fs-sm fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {s.periods}
                        </td>
                      )}
                      <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.days}</td>
                      <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {s.withLeave > 0
                          ? <Badge bg="warning" text="dark" className="fs-xs">{s.withLeave}</Badge>
                          : <span className="text-secondary">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
        <Card.Footer className="fs-xs text-secondary">
          {t.academic.absence.footerHint}
        </Card.Footer>
      </Card>
    </React.Fragment>
  )
}
